import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [orderNumber, setOrderNumber] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const poll = useCallback(async () => {
    if (!sessionId) { setStatus("error"); return; }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      setOrderNumber(data.order_number);
      if (data.payment_status === "paid") { setStatus("paid"); return; }
      if (data.status === "expired" || attempts > 10) { setStatus("error"); return; }
      setTimeout(() => setAttempts((a) => a + 1), 2000);
    } catch { setStatus("error"); }
  }, [sessionId, attempts]);

  useEffect(() => { poll(); }, [poll]);

  return (
    <div className="max-w-lg mx-auto px-5 py-28 text-center">
      {status === "checking" && <><Loader2 size={48} className="mx-auto animate-spin text-zinc-400" /><h1 className="font-display text-2xl font-bold mt-6">Confirming your payment...</h1></>}
      {status === "paid" && (
        <div data-testid="payment-success">
          <CheckCircle2 size={56} className="mx-auto text-green-600" />
          <h1 className="font-display text-3xl font-bold mt-6">Order confirmed!</h1>
          <p className="text-zinc-500 mt-2">Your order <span className="font-mono font-semibold text-zinc-900">{orderNumber}</span> is paid and entering fulfillment.</p>
          <div className="flex gap-3 justify-center mt-8">
            <Button asChild className="rounded-full"><Link to="/account">View my orders</Link></Button>
            <Button asChild variant="outline" className="rounded-full"><Link to="/shop">Keep shopping</Link></Button>
          </div>
        </div>
      )}
      {status === "error" && (
        <div data-testid="payment-error">
          <XCircle size={56} className="mx-auto text-red-500" />
          <h1 className="font-display text-3xl font-bold mt-6">Payment not confirmed</h1>
          <p className="text-zinc-500 mt-2">If you were charged, your order will appear in your account shortly.</p>
          <Button asChild className="rounded-full mt-8"><Link to="/account">Check my orders</Link></Button>
        </div>
      )}
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="max-w-lg mx-auto px-5 py-28 text-center">
      <XCircle size={56} className="mx-auto text-zinc-400" />
      <h1 className="font-display text-3xl font-bold mt-6">Checkout cancelled</h1>
      <p className="text-zinc-500 mt-2">Your cart is saved. You can complete your purchase anytime.</p>
      <Button asChild className="rounded-full mt-8" data-testid="back-to-cart"><Link to="/cart">Return to cart</Link></Button>
    </div>
  );
}
